import config from 'Cloud/config';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { openFastCheckout as openFastCheckoutAction, openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { productsController } from 'reactApp/modules/products/products.controller';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { waitForUserUpdate } from 'reactApp/modules/user/waitForUserUpdate';
import { store } from 'reactApp/store';
import { noopVoid } from 'reactApp/utils/helpers';
import { openSubscriptionsPage } from 'reactApp/utils/openHelpers';

const BILLING_ENABLED = config.get('user.cloud.billing.enabled');

const openFastCheckout = ({
    productId,
    onSuccess = noopVoid,
    afterUpdate = noopVoid,
    onClose = noopVoid,
    onNoTariff = noopVoid,
    paySource,
    hideSwitchSidebarBlock = false,
}) => {
    return new Promise((resolve) => {
        const afterUpdateCallback = () => {
            afterUpdate();
            resolve(true);
        };

        const onCloseCallback = () => {
            onClose();
            resolve(false);
        };

        store.dispatch(
            openFastCheckoutAction({
                productId,
                onSuccess,
                onClose: onCloseCallback,
                onNoTariff,
                afterUpdate: afterUpdateCallback,
                paySource,
                hideSwitchSidebarBlock,
            })
        );
    });
};

const openTariff = ({ id, onSuccess, afterUpdate = noopVoid, source = '', paySource, title, description, isMobile = false }) => {
    return new Promise((resolve) => {
        const onSuccessCallback = () => {
            onSuccess();
            waitForUserUpdate().then(() => {
                afterUpdate();
                resolve(true);
            });
        };

        const onClose = () => {
            resolve(false);
        };

        store.dispatch(
            openTariffBuy({
                productId: id,
                onSuccess: onSuccessCallback,
                onClose,
                source,
                paySource,
                title,
                paymentDescription: description,
                isMobile,
            })
        );
    });
};

interface RequestPaymentProps {
    tariff?: string;
    productId?: string;
    showTariffList?: boolean;
    showTariffListOnNoTariffs?: boolean;
    forceFastCheckout?: boolean;
    onSuccess?: () => void;
    afterUpdate?: () => void;
    onClose?: () => void;
    goToSubscriptions?: typeof openSubscriptionsPage;
    isMobile?: boolean;
    source?: string;
    paySource?: string;
    query?: string;
    title?: string;
    description?: string;
}

export const requestPayment = (props: RequestPaymentProps = {}) => {
    const {
        tariff,
        productId,
        forceFastCheckout, // TODO: починить флаг, https://jira.vk.team/browse/CLOUDWEB-18916
        paySource,
        query,
        showTariffList = false,
        showTariffListOnNoTariffs = false,
        onSuccess = noopVoid,
        afterUpdate = noopVoid,
        onClose = noopVoid,
        goToSubscriptions = openSubscriptionsPage,
        isMobile = false,
        source = '',
        title = '',
        description = '',
    } = props;
    const isPhone = EnvironmentSelectors.isMobile();

    if (!BILLING_ENABLED) {
        return Promise.resolve();
    }

    if (isMobile) {
        goToSubscriptions({ inSameTab: true, query: tariff ? `action=request-payment&productId=${tariff}` : '' });
        return Promise.resolve();
    }

    if (!(tariff || productId) && !forceFastCheckout) {
        goToSubscriptions({ inSameTab: true, anchor: 'special' });
        return Promise.resolve();
    }

    return productsController
        .loadProducts()
        .then(() => {
            const product = ProductsSelectors.getProductById(store.getState(), productId);

            if (product && product.hasTrial) {
                return openFastCheckout({ productId, onSuccess, afterUpdate, onClose, paySource, hideSwitchSidebarBlock: true });
            }

            if (productId) {
                return openTariff({
                    id: productId,
                    onSuccess,
                    afterUpdate,
                    source,
                    paySource,
                    title,
                    description,
                    isMobile: isPhone,
                });
            }

            const onNoTariff = showTariffListOnNoTariffs ? () => openSubscriptionsPage({ query }) : undefined;

            if (!showTariffList || tariff) {
                return openFastCheckout({ productId: tariff, onSuccess, afterUpdate, onClose, paySource, onNoTariff });
            }

            openSubscriptionsPage({ query });
        })
        .catch(() => openSubscriptionsPage({ query }));
};
